import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { selectors as licenseSelectors } from 'store/licenseManager'
import {
  operations as reportsOperations,
  selectors as reportsSelectors,
} from 'store/reportsManager'
import {
  sortAndPaginateData,
  TIER2_LESSON_COMPLETION_SORT_CONFIG,
} from 'store/reportsManager/helpers'
import {
  TABLE_PAGE_SIZE,
  TABLE_URL_PARAMS,
  TIER2_GRADE_BAND_KEYS,
  TIER2_LESSON_COMPLETION_SORT_KEYS,
} from 'store/reportsManager/constants'
import { useValidatedTableParams } from 'hooks/useValidatedTableParams'
import { createTableValidators } from 'hooks/useValidatedTableParams/createValidators'
import { navigationEvent } from 'layers/navigation/store/operations'
import NavigationHelper from 'layers/navigation/navigationHelper'
import K8Tier2LessonCompletion from './component'
import ContentEntryDataGetter from 'layers/content/Hocs/ContentEntryDataGetter'
import withApiError from 'layers/errorHandling/apiError/component'
import {
  ERROR_TYPE_CONTENTFUL,
  SHOW_CONTENT_ON_401,
} from 'layers/errorHandling/apiError'

// Helper function to derive gradeBand from router location
// Special case: 'interventions' path maps to K-2 grade band
const getGradeBandFromLocation = location => {
  if (!location?.pathname) {
    return TIER2_GRADE_BAND_KEYS.K_2
  }

  const pathParts = location.pathname.split('/')
  const lastPathSegment = pathParts[pathParts.length - 1]

  if (lastPathSegment === 'small-group-lesson-completion') {
    return TIER2_GRADE_BAND_KEYS.K_2
  }

  if (lastPathSegment) {
    return lastPathSegment
  }

  return TIER2_GRADE_BAND_KEYS.K_2
}

const K8Tier2LessonCompletionContainer = ({
  activeAdminK8Sites,
  adminEmail,
  classDetailsReport,
  classInstance,
  fetchK8ReportClassDetails,
  fetchTier2LessonCompletionData,
  gradeBand,
  isFetchingLessonCompletion,
  lastUpdatedAt,
  location,
  navigationEvent,
  reportsError,
  resetSelectedProgram,
  selectedProgram,
  tier2SiteLessonCompletion,
  updateSelectedProgram,
}) => {
  // Use URL search params hook with defaults
  // Only add sort/pagination params to URL if there are multiple sites
  // If there are fewer classes than page size, exclude pagination params from URL
  const isMultiSite = activeAdminK8Sites?.length > 1
  const needsSorting =
    Array.isArray(tier2SiteLessonCompletion) &&
    tier2SiteLessonCompletion.length > 1 // Only need sorting if more than 1 item
  const needsPagination =
    needsSorting && tier2SiteLessonCompletion.length > TABLE_PAGE_SIZE

  const validSiteIds = useMemo(() => {
    if (!Array.isArray(activeAdminK8Sites) || activeAdminK8Sites.length === 0) {
      return null
    }
    return activeAdminK8Sites.map(site => site?.id?.toString()).filter(Boolean)
  }, [activeAdminK8Sites])

  // Create validators with defaults and validation logic
  const validSortKeys = useMemo(
    () => Object.keys(TIER2_LESSON_COMPLETION_SORT_CONFIG),
    [],
  )

  // Extract valid class instances from data for class validator
  const validClassInstances = useMemo(() => {
    if (!Array.isArray(tier2SiteLessonCompletion)) {
      return null
    }
    return tier2SiteLessonCompletion
      .map(item => item?.classInstance)
      .filter(Boolean)
  }, [tier2SiteLessonCompletion])

  const validators = useMemo(
    () =>
      createTableValidators({
        validSortKeys,
        validSiteIds,
        validClassInstances,
        defaultSortKey: TIER2_LESSON_COMPLETION_SORT_KEYS.SMALL_GROUPS,
        defaultSiteId: activeAdminK8Sites?.[0]?.id?.toString(),
      }),
    [activeAdminK8Sites, validSiteIds, validSortKeys, validClassInstances],
  )

  // Managed table param keys - only table params that should be in URL
  // Order: site first, then sort keys, then pagination keys, then class
  // Use TABLE_URL_PARAMS constants - hook converts to validator keys internally
  // Class param is included if:
  //   1. Validator exists (when validClassInstances is available), AND
  //   2. Either:
  //      - classInstance is not in URL (validator can manage it), OR
  //      - classInstance is in URL AND is valid (in validClassInstances)
  // If classInstance is invalid, don't include it in managedTableParamKeys
  // This prevents the hook from trying to manage it (which would trigger URL updates and error clearing)
  const managedTableParamKeys = useMemo(() => {
    const keys = []
    if (isMultiSite) {
      keys.push(TABLE_URL_PARAMS.SITE_ID) // Site first
    }
    if (needsSorting) {
      keys.push(TABLE_URL_PARAMS.SORT_KEY, TABLE_URL_PARAMS.ORDER_BY) // Then sort
    }
    if (needsPagination) {
      keys.push(TABLE_URL_PARAMS.CURRENT_PAGE) // Then pagination (pageSize is always TABLE_PAGE_SIZE, not in URL)
    }
    // Add class if validator exists
    if (validators.classInstance) {
      // If classInstance is in URL, only include if it's valid
      // This prevents managing invalid class params (which would trigger URL updates and error clearing)
      if (classInstance) {
        // Check if classInstance is valid (in validClassInstances)
        const isValidClass =
          Array.isArray(validClassInstances) &&
          validClassInstances.includes(classInstance)
        if (isValidClass) {
          keys.push(TABLE_URL_PARAMS.CLASS_INSTANCE) // Valid class - include it
        }
        // Invalid class - don't include it (let it be removed naturally or stay as unmanaged param)
      } else {
        // No classInstance in URL - include it so validator can manage it
        keys.push(TABLE_URL_PARAMS.CLASS_INSTANCE)
      }
    }
    return keys
  }, [
    needsSorting,
    needsPagination,
    isMultiSite,
    validators.classInstance,
    classInstance,
    validClassInstances,
  ])

  // Determine if managedTableParamKeys is settled (data is hydrated and stable)
  // When tier2SiteLessonCompletion is an array, we can compute needsSorting/needsPagination accurately
  const isSettled = Array.isArray(tier2SiteLessonCompletion)

  const { validated, updateUrlParams } = useValidatedTableParams({
    validators,
    managedParamKeys: managedTableParamKeys,
    isSettled,
  })

  // Use validated values directly (no derivation needed!)
  // New hook returns values with validator keys (not TABLE_URL_PARAMS constants)
  // Fallback to validator defaults if param is not in managedTableParamKeys (e.g., single site scenario)
  // With two-phase initialization, validated always has values for managed params once managedTableParamKeys is determined
  const sortKey = validated.sortKey ?? validators.sortKey?.default
  const orderBy = validated.orderBy ?? validators.orderBy?.default
  const currentPage = validated.currentPage ?? validators.currentPage?.default
  const pageSize = TABLE_PAGE_SIZE // Always use TABLE_PAGE_SIZE, not in URL
  // siteId needs extra fallback since activeAdminK8Sites might change after validators are created
  const siteId =
    validated.siteId ??
    validators.siteId?.default ??
    activeAdminK8Sites?.[0]?.id
  // Use validated.classInstance instead of raw classInstance from props
  // This ensures invalid class params don't trigger fetches
  const validatedClassInstance = validated.classInstance

  // Determine if we should fetch class details
  // Note: We don't require entry to be loaded - it's loaded asynchronously via ContentEntryDataGetter
  // and shouldn't block fetching class details
  // Use validatedClassInstance instead of raw classInstance to prevent fetching invalid classes
  const classDetailsShouldFetch =
    validatedClassInstance && siteId && !classDetailsReport && !reportsError

  // Sort and paginate the data in the container using useMemo
  const sortedAndPaginatedLessonCompletionData = useMemo(() => {
    if (!Array.isArray(tier2SiteLessonCompletion)) {
      return tier2SiteLessonCompletion // Return null or {} from createSelectorState
    }

    return sortAndPaginateData({
      data: tier2SiteLessonCompletion,
      sortConfig: TIER2_LESSON_COMPLETION_SORT_CONFIG,
      sortKey,
      orderBy,
      currentPage,
      pageSize,
    })
  }, [tier2SiteLessonCompletion, sortKey, orderBy, currentPage, pageSize])

  // Compute totals for pagination/footer
  const totalItems = Array.isArray(tier2SiteLessonCompletion)
    ? tier2SiteLessonCompletion.length
    : 0
  const totalPages = pageSize
    ? Math.max(1, Math.ceil(totalItems / pageSize))
    : 1

  // Clamp currentPage to totalPages if out of bounds and update URL
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      updateUrlParams({ currentPage: totalPages }, 'replace')
    }
  }, [currentPage, totalPages, updateUrlParams])

  // Count groups that haven't completed any lessons
  const incompleteGroupsCount = Array.isArray(tier2SiteLessonCompletion)
    ? tier2SiteLessonCompletion.filter(item => (item?.lessonsDone ?? 0) <= 0)
        .length
    : 0

  // Initialize selectedProgram (hook handles URL sync)
  useEffect(() => {
    if (!selectedProgram) {
      updateSelectedProgram({ key: 'K8', name: 'K8' })
    }
  }, [selectedProgram, updateSelectedProgram])

  useEffect(() => {
    const cleanup = async () => {
      await resetSelectedProgram()
    }
    return cleanup
  }, [])

  // Fetch lesson completion data when drilling into a specific site + grade band
  useEffect(() => {
    if (!siteId || !gradeBand || !adminEmail || !selectedProgram) {
      return
    }

    // Check if data is hydrated for this specific gradeBand
    // Use the base selector result (tier2SiteLessonCompletion) for the check
    const isDataHydrated = Array.isArray(tier2SiteLessonCompletion)
    const isFetchingOrError =
      tier2SiteLessonCompletion !== null && !isDataHydrated

    // Fetch if:
    // 1. Not currently fetching/error
    // 2. Data is not hydrated (null) - this happens when gradeBand changes
    if (!isFetchingLessonCompletion && !isFetchingOrError && !isDataHydrated) {
      fetchTier2LessonCompletionData({
        adminEmail,
        siteId,
        program: selectedProgram.key,
        gradeBand,
      })
    }
  }, [
    siteId,
    gradeBand, // This triggers the effect when gradeBand changes
    adminEmail,
    selectedProgram,
    tier2SiteLessonCompletion, // Use base selector result
    isFetchingLessonCompletion,
    fetchTier2LessonCompletionData,
  ])

  // Create navigation handler using factory
  // Includes all managed params from managedTableParamKeys (table params + class if validator exists)
  const navigationHandler = useMemo(
    () =>
      NavigationHelper.createNavigationHandler({
        managedParamKeys: managedTableParamKeys,
        location,
        navigationEvent,
      }),
    [managedTableParamKeys, location, navigationEvent],
  )

  // Helper to build params object from current values + updates
  // Converts validator keys to URL param names and merges with updates
  // Maintains consistent parameter order: site, sort, paginate, class
  const buildParams = useMemo(
    () => (updates = {}) => {
      const params = {}
      const searchParams = new URLSearchParams(location?.search || '')

      // Preserve unmanaged params from current URL (add these first, before managed params)
      searchParams.forEach((value, key) => {
        if (!managedTableParamKeys.includes(key) && value) {
          params[key] = value
        }
      })

      if (managedTableParamKeys.includes(TABLE_URL_PARAMS.SITE_ID)) {
        params[TABLE_URL_PARAMS.SITE_ID] = updates.siteId ?? siteId
      }
      if (managedTableParamKeys.includes(TABLE_URL_PARAMS.SORT_KEY)) {
        params[TABLE_URL_PARAMS.SORT_KEY] = updates.sortKey ?? sortKey
      }
      if (managedTableParamKeys.includes(TABLE_URL_PARAMS.ORDER_BY)) {
        params[TABLE_URL_PARAMS.ORDER_BY] = updates.orderBy ?? orderBy
      }
      if (managedTableParamKeys.includes(TABLE_URL_PARAMS.CURRENT_PAGE)) {
        params[TABLE_URL_PARAMS.CURRENT_PAGE] =
          updates.currentPage ?? currentPage
      }
      // Handle classInstance if it's in managedTableParamKeys (validator exists)
      if (managedTableParamKeys.includes(TABLE_URL_PARAMS.CLASS_INSTANCE)) {
        // Explicitly check if classInstance is in updates to allow removing it (null/undefined)
        if ('classInstance' in updates) {
          // If explicitly set (even if null), use that value
          if (updates.classInstance) {
            params[TABLE_URL_PARAMS.CLASS_INSTANCE] = updates.classInstance
          }
          // If null/undefined, don't add class param (removes it from URL)
        } else if (classInstance) {
          // Not in updates, preserve current value
          params[TABLE_URL_PARAMS.CLASS_INSTANCE] = classInstance
        }
      }

      return params
    },
    [
      location,
      managedTableParamKeys,
      siteId,
      sortKey,
      orderBy,
      currentPage,
      classInstance,
    ],
  )

  // Handler for pagination/sort changes - calls navigationHandler with updated params
  // Preserves current class param (if drawer is open)
  const handlePaginationSortChange = ({ newSortKey, newOrderBy, newPage }) => {
    const params = buildParams({
      sortKey: newSortKey,
      orderBy: newOrderBy,
      currentPage: newPage,
      // classInstance not passed - buildParams will preserve current value
    })
    navigationHandler(params, NavigationHelper.types.PUSH)
  }

  // Handler for opening/closing the class drawer - calls navigationHandler with classInstance
  // Pass null/undefined to close the drawer (removes class param)
  const handleToggleClassDrawer = (classInstanceToToggle, siteIdToUse) => {
    const params = buildParams({
      siteId: siteIdToUse || siteId,
      classInstance: classInstanceToToggle || null, // null removes the param
    })
    navigationHandler(params, NavigationHelper.types.REPLACE)
  }

  // Fetch class details when class query param is present
  useEffect(() => {
    if (classDetailsShouldFetch) {
      fetchK8ReportClassDetails(siteId, validatedClassInstance)
    }
  }, [
    classDetailsShouldFetch,
    siteId,
    validatedClassInstance,
    fetchK8ReportClassDetails,
  ])

  return !siteId ? null : (
    <K8Tier2LessonCompletion
      classDetailsReport={classDetailsReport}
      classInstance={classInstance}
      currentPage={currentPage}
      gradeBand={gradeBand}
      handleOpenClassDrawer={handleToggleClassDrawer}
      handlePaginationSortChange={handlePaginationSortChange}
      incompleteGroupsCount={incompleteGroupsCount}
      isFetchingLessonCompletion={isFetchingLessonCompletion}
      lastUpdatedAt={lastUpdatedAt}
      orderBy={orderBy}
      pageSize={pageSize}
      selectedProgram={selectedProgram}
      siteId={siteId}
      sortKey={sortKey}
      sortedAndPaginatedLessonCompletionData={
        sortedAndPaginatedLessonCompletionData
      }
      totalItems={totalItems}
      totalPages={totalPages}
    />
  )
}

const mapStateToProps = (state, ownProps) => {
  const {
    reportsManager: { selectedProgram, error: reportsError },
    router: { location },
    userProfileManager: {
      profile: { email: adminEmail },
    },
  } = state
  const activeAdminK8Sites = licenseSelectors.selectActiveAdminK8Sites(state)

  // Derive gradeBand from router location using helper
  const gradeBand = getGradeBandFromLocation(location)

  const searchParams = new URLSearchParams(location?.search || '')
  const siteId = parseInt(searchParams.get(TABLE_URL_PARAMS.SITE_ID) || '0', 10)

  // Get classInstance from query params (used for class drawer)
  // Use location.query if available (connected-react-router), otherwise parse from search
  const classInstance =
    location?.query?.class || searchParams.get('class') || null
  const classDetailsReport = classInstance
    ? reportsSelectors.selectClassDetailsReport(state, classInstance)
    : null

  // Note: classDetailsShouldFetch is calculated in component body using validatedClassInstance
  // (see below after validatedClassInstance is defined)

  // Use the base selector instead of the sorted one
  // Sorting/pagination will be done in the container with useMemo
  const tier2SiteLessonCompletion =
    siteId && gradeBand
      ? reportsSelectors.selectTier2SiteLessonCompletion(
          state,
          siteId,
          gradeBand,
        )
      : null
  const lastUpdatedAt =
    state.reportsManager.admin.k8.tier2.lessonCompletion.data[siteId]
      ?.lastUpdatedAt
  // Derive isFetching from selector return value and global error
  // {} = fetching or error (prevents refetching)
  // null = not hydrated (should fetch)
  // Array = hydrated (ready to use)
  const isFetchingLessonCompletion =
    siteId && gradeBand
      ? tier2SiteLessonCompletion !== null &&
        !Array.isArray(tier2SiteLessonCompletion) &&
        !reportsError
      : false

  return {
    activeAdminK8Sites,
    adminEmail,
    classDetailsReport,
    classInstance,
    gradeBand,
    isFetchingLessonCompletion,
    lastUpdatedAt,
    location,
    reportsError,
    selectedProgram,
    tier2SiteLessonCompletion,
  }
}

const mapDispatchToProps = {
  fetchK8ReportClassDetails:
    reportsOperations.fetchK8ReportClassDetailsBySiteId,
  fetchTier2LessonCompletionData:
    reportsOperations.fetchTier2LessonCompletionData,
  navigationEvent,
  resetSelectedProgram: reportsOperations.resetSelectedProgram,
  updateSelectedProgram: reportsOperations.updateSelectedProgram,
}

const mapper = entry => {
  const { displayTitle, onboardingSnapshot } = entry || {}
  return {
    displayTitle,
    onboardingSnapshot,
  }
}

const options = {
  entryId: '5oqdbnI289AADFVmm2sJyx',
  include: 5,
  mapper,
  spread: true,
}

K8Tier2LessonCompletionContainer.propTypes = {
  activeAdminK8Sites: PropTypes.array,
  adminEmail: PropTypes.string,
  classDetailsReport: PropTypes.object,
  classInstance: PropTypes.string,
  fetchK8ReportClassDetails: PropTypes.func,
  fetchTier2LessonCompletionData: PropTypes.func,
  gradeBand: PropTypes.string,
  isFetchingLessonCompletion: PropTypes.bool,
  lastUpdatedAt: PropTypes.string,
  location: PropTypes.object,
  navigationEvent: PropTypes.func,
  reportsError: PropTypes.object,
  resetSelectedProgram: PropTypes.func,
  selectedProgram: PropTypes.object,
  tier2SiteLessonCompletion: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object, // {} from createSelectorState when fetching/error
    PropTypes.oneOf([null]), // null when not hydrated
  ]),
  updateSelectedProgram: PropTypes.func,
}

export default ContentEntryDataGetter(
  withApiError(
    K8Tier2LessonCompletionContainer,
    [ERROR_TYPE_CONTENTFUL],
    [SHOW_CONTENT_ON_401],
  ),
  options,
  connect(mapStateToProps, mapDispatchToProps),
)
